import { Component } from 'react';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import ColorRangeSelect from 'components/atoms/ColorRangeSelect';
import DropList from 'components/atoms/DropList';
import Form from 'components/atoms/Form';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Label from 'components/atoms/Label';
import ValueSlider from 'components/atoms/ValueSlider';
import { LAYERS } from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
import { SetLayerAction, setLayerAction } from 'store/actions/layoutActions';
import { LayerType, RootState } from 'types';
import { ActionsType } from 'types/system';
import { clone, setKey } from 'utils/formHandlers';
import { translate } from 'utils/translate';

interface ScreenGridSettingModelProps {
    children?: string | JSX.Element | JSX.Element[];
    language: string;
    id?: string;
    data: any;
    actions: ActionsType | undefined;
    layers: LayerType;
    setLayerAction: SetLayerAction;
}

interface ScreenGridSettingModelState {
    screenGridSettings: any;
}

class ScreenGridSettingModel extends Component<ScreenGridSettingModelProps, ScreenGridSettingModelState> {
    constructor(props: ScreenGridSettingModelProps) {
        super(props);
        this.state = {
            screenGridSettings: clone(props.layers[props.data.layer]),
        };
    }

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        this.props.setLayerAction(this.props.data.layer, this.state.screenGridSettings);
        this.props.actions?.onCancel();
    };

    handleFormReset = () => {
        this.setState({
            screenGridSettings: clone(LAYERS[this.props.data.layer]),
        });
    };

    handleFormApply = () => {
        this.props.setLayerAction(this.props.data.layer, clone(this.state.screenGridSettings));
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    handleChange = (name, value) => {
        const { screenGridSettings } = this.state;
        setKey(screenGridSettings, name, value);
        this.setState({
            screenGridSettings,
        });
    };
    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded">
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('Screen grid settings')} </div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>
                    <div className=" mt-5">
                        <FormGroup required={false}>
                            <ValueSlider
                                name="options.cellSizePixels"
                                range={[0, 100]}
                                label={translate('cellSize pixels')}
                                decimal={0}
                                onChange={this.handleChange}
                                value={this.state.screenGridSettings.options.cellSizePixels}
                                unit={translate('pixel')}
                            ></ValueSlider>
                        </FormGroup>
                        <FormGroup required={false}>
                            <ValueSlider
                                name="options.opacity"
                                range={[0, 100]}
                                label={translate('opacity')}
                                decimal={0}
                                onChange={(name, value) => {
                                    this.handleChange(name, value / 100);
                                }}
                                value={this.state.screenGridSettings.options.opacity * 100}
                                unit={translate('%')}
                            ></ValueSlider>
                        </FormGroup>
                        <FormGroup required={false}>
                            <Label>{translate('By')}:</Label>
                            <DropList  capitalize
                                name="options.groupBy"
                                onChange={this.handleChange}
                                options={[
                                    { label: translate('coordinates'), value: 'coordinates' },
                                    { label: translate('drift'), value: 'drift_angle_deg' },
                                    { label: translate('crot'), value: 'crot_deg_min' },
                                    { label: translate('cog'), value: 'cog_deg' },
                                    { label: translate('sog'), value: 'sog_mps' },
                                    { label: translate('rot'), value: 'rot_deg_min' },
                                ]}
                                value={this.state.screenGridSettings.options.groupBy}
                            ></DropList>
                        </FormGroup>
                        <FormGroup required={false}>
                            <Label>{translate('color range')}:</Label>
                            <ColorRangeSelect
                                onChange={this.handleChange}
                                name="options.colorRange"
                                value={this.state.screenGridSettings.options.colorRange}
                            ></ColorRangeSelect>
                        </FormGroup>
                    </div>
                    <div className="flex justify-end">
                        <div className="mt-5 flex gap-3">
                            <Button
                                className="px-4 py-2 font-bold border border-gray-400"
                                onClick={this.handleFormCancel}
                            >
                                {translate('Cancel')}
                            </Button>
                            <Button className="px-4 py-1   bg-primary-clear text-white" onClick={this.handleFormReset}>
                                {translate('Reset')}
                            </Button>{' '}
                            <Button className="px-4 py-1   bg-primary-clear text-white" onClick={this.handleFormApply}>
                                {translate('Apply')}
                            </Button>
                            <Button type="submit" className="px-4 py-2 bg-primary-submit rounded-md text-white">
                                {translate('Save')}
                            </Button>
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}

const ScreenGridSettingModelStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    layers: state.layout.layers,
});

const ScreenGridSettingModelDispatchToProps = (dispatch: Dispatch) => ({
    setLayerAction: bindActionCreators(setLayerAction, dispatch),
});

export default connect(
    ScreenGridSettingModelStateToProps,
    ScreenGridSettingModelDispatchToProps
)(ScreenGridSettingModel);
